import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DeliveryLeadTime } from '@1po/1po-bff-fe-spec/generated/backoffice/delivery_lead_time/response/DeliveryLeadTimeResponse';
import {
  ReferenceStock,
  WarehouseStockDetail,
} from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import { DMSReferenceStock } from '@1po/1po-bff-fe-spec/generated/dms/model/DMSReferenceStock';
import {
  DeliveryLeadTime as OrderDeliveryLeadTime,
  Warehouse,
  WarehouseDeliveryStatus,
} from '@1po/1po-bff-fe-spec/generated/order/response/GetDealerOrderPageResponse';
import { SpinSize } from 'antd-v5/lib/spin';
import { RootState } from 'app/AppStore';
import { Ban, ExclamationTriangleIcon, TruckIcon } from 'assets/icons';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import Popover from 'components/Popover';
import { isBulkCertasOil, isCanCertasOil, isCertasOil, isDrumCertasOil } from 'components/StockInfo/catalogStockUtils';
import { DelayedStock, isDelayedStock } from 'components/StockInfo/DelayedStock';
import { EmptyStock, isEmptyStock } from 'components/StockInfo/EmptyStock';
import { DMSStockText, getIcon, StockText, StockTextWithDeliveryType } from 'components/StockInfo/StockTexts';
import PrivateComponent from 'composers/PrivateComponent';
import { useIsOilChunkUserR1 } from 'domains/appContext/AppContext.requests';
import { getFilterOutOfStockReferences } from 'domains/appContext/AppContext.store';
import { getBasketReference } from 'domains/basket/Basket.store';
import { getUniversalProduct } from 'domains/catalog/Catalog.store';
import { getUserGarageWorkshopId } from 'domains/garage/Garage.store';
import { getInProgressOrder } from 'domains/order/Order.store';
import {
  getDHReference,
  getDmsStockInfo,
  getIAMReference,
  getStockInfo,
  hasMultipleOffers,
  MAX_QUANTITY_WITH_STOCKS,
} from 'domains/references';
import { useFetchReferenceStocks } from 'domains/references/References.requests';
import { getExadisStockInfoWithDeliveryToday } from 'domains/references/References.utils';
import { getUserCountry, UserRole } from 'domains/user';
import { theme } from 'styles';
import { Box, CenterFlex, Flex, Icon, Spin, Text } from 'UI';
import { getData, hasData, NO_DATA, SearchData } from 'utils';
import { PaddingBox } from './StockDisplay.styled';

export const StockInformationUnavailable = () => {
  return (
    <Flex align={'center'}>
      <Icon IconComponent={TruckIcon} size={20} mt={4} mr={5} color={theme.color.grey55} />
      <Text type="sm" color="grey55">
        <Trans i18nKey={'catalog.reference.stock.stock_noinfo'}>{'Stock information unavailable.'}</Trans>
      </Text>
    </Flex>
  );
};

const getIconMarginTop = (centered: boolean) => (centered ? 0 : 4);
const getIconMarginRight = (centered: boolean) => (centered ? 0 : 5);

const ResolveMini = ({
  component,
  small,
  centered,
  stock,
  relativeDate = new Date(),
  userCountry,
  size = 20,
  spinSize,
}: {
  component: React.ReactNode;
  stock: SearchData<ReferenceStock> | undefined;
  small: boolean;
  centered: boolean;
  relativeDate?: Date;
  userCountry: string | undefined;
  size?: number;
  spinSize?: SpinSize;
}) => {
  if (small) {
    const availableWarehouses = getData(stock?.data)
      ?.warehouses?.slice()
      .filter((w) => w.confirmedQuantity > 0);
    const currentStock = availableWarehouses && availableWarehouses?.length > 0 ? availableWarehouses[0] : undefined;

    return (
      <Popover
        content={
          <PaddingBox>
            <Flex justify={'center'}>{component}</Flex>
          </PaddingBox>
        }
        placement={'bottom'}
        arrowColor={theme.color.white}
        trigger={'hover'}
      >
        <DataContainer
          data={stock?.searchStatus}
          ErrorState={() => (
            <Icon IconComponent={ExclamationTriangleIcon} color={theme.color.warning} size={size} noPointer />
          )}
          Loading={() => (
            <Box width={size} height={size}>
              <CenterFlex>
                <Spin size={spinSize || 'large'} />
              </CenterFlex>
            </Box>
          )}
          NotFound={() => <></>}
        >
          <Box width={size} height={size}>
            <CenterFlex>
              {currentStock ? (
                <>
                  {isDelayedStock(stock?.data) ? (
                    <Icon
                      IconComponent={TruckIcon}
                      size={size}
                      mt={getIconMarginTop(centered)}
                      mr={getIconMarginRight(centered)}
                      display={'block'}
                      color={theme.color.warning}
                      noPointer
                    />
                  ) : (
                    <>
                      {getIcon(
                        centered,
                        currentStock.type,
                        currentStock.expectedDeliveryDate,
                        currentStock.depan,
                        relativeDate,
                        userCountry,
                        false,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        size,
                      )}
                    </>
                  )}
                </>
              ) : (
                <Icon IconComponent={Ban} color={theme.color.error} size={size} noPointer />
              )}
            </CenterFlex>
          </Box>
        </DataContainer>
      </Popover>
    );
  }
  return <>{component}</>;
};

interface StockInfoComponentProps {
  isApplicableToCurrentVehicle?: boolean;
  vehicleKey: string | undefined;
  referenceNumber: string;
  narrow?: boolean;
  small?: boolean;
  centered?: boolean;
  size?: number;
  spinSize?: SpinSize;
  isExadisPickUp?: boolean;
  dataCy?: string;
  fetchQuantity?: number;
  sellerNumber?: string;
  handleMultipleOffersButtonClick?: React.Dispatch<React.SetStateAction<boolean>>;
  noPviInfo?: boolean;
}

interface StockDisplayBasicProps {
  stock: SearchData<ReferenceStock> | undefined;
  quantity?: number;
  narrow?: boolean;
  small?: boolean;
  centered?: boolean;
  size?: number;
  spinSize?: SpinSize;
  userCountry: string | undefined;
  isCanOil?: boolean;
  isDrumOil?: boolean;
  isBulkOil?: boolean;
  isCertasOilFamily?: boolean;
  isExadisPickUp?: boolean;
  dataCy?: string;
  isDmsStockAvailable?: boolean;
  dmsStock?: SearchData<DMSReferenceStock> | undefined;
  isPviOnlyReference?: boolean;
}

const getValidWhs = (all: WarehouseStockDetail[]) => all.filter((w) => w.availableQuantity > -1);

const getLocalWh = (validWhs: WarehouseStockDetail[]) => validWhs.find((w) => w.type === 'LOCAL');

export const StockDisplayBasic = ({
  narrow,
  small = false,
  centered = false,
  size,
  spinSize,
  stock,
  userCountry,
  quantity,
  isCanOil,
  isDrumOil,
  isBulkOil,
  isCertasOilFamily,
  isExadisPickUp,
  dataCy,
  isDmsStockAvailable,
  dmsStock,
  isPviOnlyReference,
}: StockDisplayBasicProps) => {
  const { t } = useTranslation();
  const isOilUserR1 = useIsOilChunkUserR1();
  if (isEmptyStock(stock) && !isDmsStockAvailable) {
    return ResolveMini({
      component: <EmptyStock isExadisPickUp={isExadisPickUp} isDrumOil={isDrumOil} />,
      stock,
      small,
      centered,
      userCountry,
      size,
      spinSize,
    });
  }

  const moreThen999 = (quantity ?? 0) > MAX_QUANTITY_WITH_STOCKS && !(isOilUserR1 && isCertasOilFamily);
  if (isDelayedStock(stock?.data) || moreThen999) {
    return ResolveMini({
      component: (
        <DelayedStock
          isDrumOil={isDrumOil}
          isBulkOil={isBulkOil}
          relativeDate={new Date()}
          isCertasOilFamily={isCertasOilFamily}
        />
      ),
      stock,
      small,
      centered,
      userCountry,
      size,
      spinSize,
    });
  }

  const containsValidWarehouse =
    (stock?.data?.warehouses ?? []).filter((warehouse) => warehouse.type !== 'INVALID').length > 0;

  const isLoadingNormalStock = stock?.searchStatus === 'loading';
  const isDmsLoading = dmsStock?.searchStatus === 'loading' && !isLoadingNormalStock;
  const hasDmsWarehouses = dmsStock?.data?.warehouses && dmsStock.data.warehouses.length > 0;
  const shouldShowDmsSection = (isDmsStockAvailable && hasDmsWarehouses) || isDmsLoading;

  const Stocks = () => (
    <Flex direction={'column'}>
      <PrivateComponent
        render={() => (
          <>
            {shouldShowDmsSection && (
              <DataContainer
                data={dmsStock?.searchStatus}
                NotFound={() => <></>}
                ErrorState={() => <></>}
                Loading={() => (
                  <CenterFlex>
                    <Spin size={spinSize || 'small'} />
                  </CenterFlex>
                )}
              >
                {isDmsStockAvailable ? (
                  <Flex direction={'column'} data-cy={dataCy}>
                    {hasData(dmsStock?.data) &&
                      dmsStock?.data?.warehouses?.map((warehouse, index) => (
                        <Flex key={`dms_stock_display_warehouse_${index}`} size={0}>
                          <DMSStockText stock={dmsStock?.data} warehouse={warehouse} />
                        </Flex>
                      ))}
                  </Flex>
                ) : (
                  <></>
                )}
              </DataContainer>
            )}
          </>
        )}
        requiredRights={[UserRole.COMMAND, UserRole.CONNECT_COMMANDE]}
      />
      <PrivateComponent
        render={() => (
          <DataContainer
            data={stock?.searchStatus}
            NotFound={() => <></>}
            ErrorState={() => (
              <ErrorWithLabel
                label={t('common.stock.backend_error', 'Stocks temporarily unavailable, please try again later.')}
                narrow
              />
            )}
            Loading={() => (
              <CenterFlex>
                <Spin size={spinSize || 'small'} />
              </CenterFlex>
            )}
          >
            {containsValidWarehouse ? (
              <Flex direction={'column'} data-cy={dataCy}>
                {hasData(stock?.data) &&
                  stock?.data.warehouses
                    .slice()
                    .filter((wh, _index, all) => {
                      if (!isCertasOilFamily) {
                        return true;
                      }
                      if (!isOilUserR1 && !isBulkOil) {
                        return true;
                      }

                      const validWhs = getValidWhs(all);
                      const localWh = getLocalWh(validWhs);
                      const shouldSkipLocal = validWhs.length > 1 && localWh && localWh.confirmedQuantity > 0;

                      return !(shouldSkipLocal && wh.type === 'LOCAL');
                    })
                    .sort((warehouseA, warehouseB) => {
                      if (warehouseA.primary === true) {
                        return -1;
                      } else if (warehouseB.primary === true) {
                        return 1;
                      } else {
                        return 0;
                      }
                    })
                    .map((s, index) => (
                      <Flex key={`stock_display_warehouse_${index}`} size={0}>
                        <StockText
                          narrow={narrow}
                          stock={stock?.data}
                          quantity={quantity}
                          warehouse={s}
                          displayIcon
                          displayIndex={index}
                          isCanOil={isCanOil}
                          isDrumOil={isDrumOil}
                          isBulkOil={isBulkOil}
                          isCertasOilFamily={isCertasOilFamily}
                          isExadisPickUp={isExadisPickUp}
                          isPviOnlyReference={isPviOnlyReference}
                        />
                      </Flex>
                    ))}
              </Flex>
            ) : (
              <StockInformationUnavailable />
            )}
          </DataContainer>
        )}
        requiredRights={[UserRole.COMMAND, UserRole.CONNECT_COMMANDE]}
      />
    </Flex>
  );

  return ResolveMini({
    component: <Stocks />,
    stock,
    small,
    centered,
    userCountry,
    size,
    spinSize,
  });
};

const StockDisplay = ({
  isApplicableToCurrentVehicle = false,
  vehicleKey,
  referenceNumber,
  narrow,
  small = false,
  centered = false,
  size,
  spinSize,
  isExadisPickUp = false,
  dataCy,
  fetchQuantity,
  sellerNumber,
  handleMultipleOffersButtonClick,
  noPviInfo = false,
}: StockInfoComponentProps) => {
  const filterOutOfStockReferences = useSelector(getFilterOutOfStockReferences);
  const isPviOnlyReference = !noPviInfo && filterOutOfStockReferences.includes(referenceNumber);

  const stockInfo = useSelector((state: RootState) => getStockInfo(state, referenceNumber, sellerNumber));
  const dmsStockInfo = useSelector((state: RootState) => getDmsStockInfo(state, referenceNumber));
  const stock = useMemo(() => (isExadisPickUp ? getExadisStockInfoWithDeliveryToday(stockInfo) : stockInfo), [
    stockInfo,
    isExadisPickUp,
  ]);

  const referenceWithMultipleOffers = useSelector((state: RootState) => hasMultipleOffers(state, referenceNumber));

  const IAMReference = useSelector((state: RootState) => getIAMReference(state, { referenceNumber, vehicleKey }));

  const universalProductReference = useSelector((state: RootState) => getUniversalProduct(state, referenceNumber));

  const dhReference = useSelector((state: RootState) => getDHReference(state, { vehicleKey, referenceNumber }));

  const userGarageWorkshopId = useSelector((state: RootState) => getUserGarageWorkshopId(state));

  const userCountry = useSelector(getUserCountry);
  const basketReference = useSelector((state: RootState) =>
    getBasketReference(state, {
      reference: {
        referenceNumber,
        sellerNumber,
      },
      vehicleKey: isApplicableToCurrentVehicle && vehicleKey ? vehicleKey : undefined,
    }),
  );
  const isOilUserR1 = useIsOilChunkUserR1();
  const basketReferenceData = getData(basketReference);

  // Resolve Certas oil fields from available sources: universal product > basket > DH reference
  const isUniversalProduct = hasData(universalProductReference);
  const universalProductData = getData(universalProductReference);
  const dhReferenceData = hasData(dhReference) ? dhReference : undefined;
  const resolvedFamilyCode = isUniversalProduct
    ? universalProductReference?.familyCode
    : basketReferenceData?.familyCode ?? dhReferenceData?.familyCode;
  const resolvedProductModelCode = isUniversalProduct
    ? universalProductReference?.productModelCode
    : basketReferenceData?.productModelCode ?? dhReferenceData?.productModelCode;
  const resolvedPackaging = isUniversalProduct
    ? universalProductData?.technicalCharacteristics
        ?.map((tc) => tc.value)
        .filter(Boolean)
        .join(' ')
    : basketReferenceData?.packaging ?? dhReferenceData?.packaging;

  const stockRequestDetail = hasData(IAMReference)
    ? {
        referenceNumber: IAMReference.referenceNumber,
        type: IAMReference.type,
        quantity: 1,
        origin: IAMReference.origin,
        supplierCode: IAMReference.supplierCode,
        referenceSource: IAMReference.referenceSource,
        isPrimaryStockRequest: isExadisPickUp,
        isCertasReference:
          isBulkCertasOil(resolvedFamilyCode, resolvedProductModelCode, resolvedPackaging) ||
          (isOilUserR1 && isCertasOil(resolvedFamilyCode, resolvedProductModelCode)) ||
          false,
        sellerNumber: IAMReference.sellerNumber,
      }
    : undefined;

  useFetchReferenceStocks(referenceNumber, stockRequestDetail, fetchQuantity);

  const dmsHasWarehouses =
    hasData(dmsStockInfo) &&
    (dmsStockInfo.data?.warehouses?.length ?? 0) > 0 &&
    dmsStockInfo.data?.warehouses?.some((w) => w.quantity > 0);
  const isDmsStockAvailable = hasData(userGarageWorkshopId) && dmsHasWarehouses;

  const isCanOil = isCanCertasOil(resolvedFamilyCode, resolvedProductModelCode, resolvedPackaging);

  const isDrumOil = isDrumCertasOil(resolvedFamilyCode, resolvedProductModelCode, resolvedPackaging);

  if (referenceWithMultipleOffers && handleMultipleOffersButtonClick) {
    return (
      <Flex align={'center'} onClick={() => handleMultipleOffersButtonClick?.(true)} maxHeight={22}>
        <Icon IconComponent={TruckIcon} size={20} mt={4} mr={5} color={theme.color.info} display={'block'} />
        <Box>
          <Text type="base" bold color="grey35" displayStyle={'default'} cursor={'pointer'}>
            <Trans i18nKey={'catalog.reference.stock.see_all_availabilities'}>{'See all availabilities'}</Trans>
          </Text>
        </Box>
      </Flex>
    );
  }

  const isBulkOil = isBulkCertasOil(resolvedFamilyCode, resolvedProductModelCode, resolvedPackaging);

  const isCertasOilFamily = isCertasOil(resolvedFamilyCode, resolvedProductModelCode);

  return (
    <StockDisplayBasic
      stock={stock}
      narrow={narrow}
      small={small}
      centered={centered}
      size={size}
      spinSize={spinSize}
      userCountry={userCountry}
      quantity={fetchQuantity ?? basketReference?.quantity}
      isCanOil={isCanOil}
      isDrumOil={isDrumOil}
      isBulkOil={isBulkOil}
      isCertasOilFamily={isCertasOilFamily}
      isExadisPickUp={isExadisPickUp}
      dataCy={dataCy}
      isDmsStockAvailable={isDmsStockAvailable}
      dmsStock={dmsStockInfo}
      isPviOnlyReference={isPviOnlyReference}
    />
  );
};

export const StockDisplayCustom = ({
  narrow,
  stock,
  quantity,
}: {
  narrow: boolean;
  stock: NO_DATA | ReferenceStock;
  quantity: number;
}) => {
  const { t } = useTranslation();
  const moreThen999 = quantity > MAX_QUANTITY_WITH_STOCKS;

  if (isDelayedStock(stock) || moreThen999) {
    return <DelayedStock />;
  }

  return (
    <DataContainer
      data={stock}
      NotFound={() => <></>}
      Loading={() => <Spin size={'small'} />}
      ErrorState={() => (
        <ErrorWithLabel
          label={t('common.stock.backend_error', 'Stocks temporarily unavailable, please try again later.')}
          narrow
        />
      )}
    >
      <Flex>
        {hasData(stock) &&
          stock.warehouses
            .slice()
            .sort((warehouseA, warehouseB) => {
              if (warehouseA.primary === true) {
                return -1;
              } else if (warehouseB.primary === true) {
                return 1;
              } else {
                return 0;
              }
            })
            .map((s, index) => (
              <StockText
                key={`stock_display_warehouse_${index}`}
                narrow={narrow}
                stock={stock}
                quantity={quantity}
                warehouse={s}
                displayIndex={index}
                displayIcon
              />
            ))}
      </Flex>
    </DataContainer>
  );
};

export const OrderStockDisplay = ({
  orderId,
  referenceNumber,
  narrow,
  warehouseDeliveryStatuses,
  quantity,
  orderDate,
  referenceName,
  isExadisPickUp,
}: {
  orderId: string;
  referenceNumber: string;
  narrow: boolean;
  warehouseDeliveryStatuses: WarehouseDeliveryStatus[] | undefined;
  quantity: number;
  orderDate: string;
  referenceName?: string;
  isExadisPickUp?: boolean;
}) => {
  const orderData = useSelector((state: RootState) => getInProgressOrder(state, orderId));

  const order = getData(orderData);

  const refFamilyCode = order?.data?.orderedReferences?.find((ref) => ref.referenceNumber === referenceNumber)
    ?.familyCode;

  const refProductModelCode = order?.data?.orderedReferences?.find((ref) => ref.referenceNumber === referenceNumber)
    ?.productModelCode;

  const isCanOil = isCanCertasOil(refFamilyCode, refProductModelCode, referenceName);

  const isDrumOil = isDrumCertasOil(refFamilyCode, refProductModelCode, referenceName);

  const isBulkOil = isBulkCertasOil(refFamilyCode, refProductModelCode, referenceName);

  const isCertasOilFamily = isCertasOil(refFamilyCode, refProductModelCode);

  const isOilUserR1 = useIsOilChunkUserR1();
  const isOilR1CanOrDrum = isOilUserR1 && (isCanOil || isDrumOil);

  if (!warehouseDeliveryStatuses || quantity > MAX_QUANTITY_WITH_STOCKS) {
    return (
      <DelayedStock
        isCanOil={isCanOil}
        isDrumOil={isDrumOil}
        isBulkOil={isBulkOil}
        relativeDate={new Date(orderDate)}
      />
    );
  }

  const mapDeliveryLeadTime = (
    warehouse: Warehouse,
    leadTime: OrderDeliveryLeadTime | undefined,
  ): DeliveryLeadTime | undefined => {
    if (!leadTime) {
      return undefined;
    }
    return {
      ...leadTime,
      warehouse,
      isDisabled: false,
    } as DeliveryLeadTime;
  };

  const mapStocks = (status: WarehouseDeliveryStatus): ReferenceStock => {
    return {
      reference: referenceNumber,
      confirmedQuantity: status.confirmedQuantity,
      warehouses: [
        {
          order: 0,
          type: status.type,
          availableQuantity: status.availableQuantity,
          confirmedQuantity: status.confirmedQuantity,
          expectedDeliveryDate: status.expectedDeliveryDate,
          deliveryInformation: status.deliveryInformation,
          depan: status.depan,
        },
      ],
    };
  };

  const statusesToDisplay = isOilR1CanOrDrum ? warehouseDeliveryStatuses.slice(0, 1) : warehouseDeliveryStatuses;

  return (
    <Flex direction={'row'}>
      <Flex direction={'column'}>
        {statusesToDisplay.map((status, index) => (
          <StockTextWithDeliveryType
            key={`${orderId}_${referenceNumber}_stockStatus_${index}`}
            displayIndex={index}
            narrow={narrow}
            stock={mapStocks(status)}
            quantity={quantity}
            warehouse={mapStocks(status).warehouses[0]}
            deliveryLeadTime={mapDeliveryLeadTime(status.type, status.deliveryLeadTime)}
            displayIcon
            relativeDate={new Date(orderDate)}
            isCanOil={isCanOil}
            isDrumOil={isDrumOil}
            isBulkOil={isBulkOil}
            isCertasOilFamily={isCertasOilFamily}
            isExadisPickUp={isExadisPickUp}
          />
        ))}
      </Flex>
    </Flex>
  );
};

export default React.memo(StockDisplay);
